<template>
  <CCard class="mt-3">
    <CCardHeader>
      <CRow class="d-flex align-items-center pl-3">
        <h5 class="text-primary mb-0">
          <i class="fa-solid fa-chart-bar mr-2" />
          Courses Overall Progress
        </h5>
      </CRow>
    </CCardHeader>

    <CCardBody>
      <div v-if="loading" class="text-center py-4">
        <CSpinner color="primary" />
      </div>

      <div v-else-if="!hasCourseData" class="text-center text-muted py-4">
        <i class="fas fa-inbox mb-2" style="font-size: 28px; opacity: 0.4;" />
        <div class="mt-1">No course enrolment data available</div>
      </div>

      <div v-else>
        <div class="d-flex mb-3" style="gap: 16px; flex-wrap: wrap;">
          <div
            v-for="item in legend"
            :key="item.label"
            class="d-flex align-items-center"
            style="gap: 5px;"
          >
            <span
              :style="`width: 10px; height: 10px; border-radius: 2px; background: ${item.color}; display: inline-block; flex-shrink: 0;`"
            />
            <small style="font-size: 12px; color: #666;">{{ item.label }}</small>
          </div>
        </div>

        <div style="position: relative; height: 300px;">
          <CChartBar
            :datasets="chartDatasets"
            :labels="chartLabels"
            :options="chartOptions"
          />
        </div>
      </div>
    </CCardBody>
  </CCard>
</template>

<script>
import { CChartBar } from "@coreui/vue-chartjs";
import course from "@/services/course";

export default {
  name: "MoodleCoursesProgressChart",

  components: {
    CChartBar,
  },

  data() {
    return {
      loading: false,
      courses: [],
      legend: [
        { label: "Enrolled", color: "#4e94ff" },
        { label: "In Progress", color: "#f2b759" },
        { label: "Completed", color: "#4caf50" },
      ],
    };
  },

  async mounted() {
    await this.loadCourses();
  },

  computed: {
    activeCourses() {
      return this.courses.filter((c) => c.enrolled_count > 0);
    },

    hasCourseData() {
      return this.activeCourses.length > 0;
    },

    chartLabels() {
      return this.activeCourses.map((c) => {
        const title = (c.course_title || "—").trim();
        return title.length > 20 ? title.substring(0, 20) + "…" : title;
      });
    },

    chartDatasets() {
      const completedData = this.activeCourses.map((c) => c.completed_count || 0);
      const inProgressData = this.activeCourses.map((c) => c.in_progress_count || 0);
      const enrolledData = this.activeCourses.map((c) => {
        const notStarted = (c.enrolled_count || 0) - (c.in_progress_count || 0) - (c.completed_count || 0);
        return notStarted > 0 ? notStarted : 0;
      });

      return [
        {
          label: "Completed",
          backgroundColor: "#4caf50",
          data: completedData,
          stack: "candidates",
        },
        {
          label: "In Progress",
          backgroundColor: "#f2b759",
          data: inProgressData,
          stack: "candidates",
        },
        {
          label: "Enrolled",
          backgroundColor: "#4e94ff",
          data: enrolledData,
          stack: "candidates",
        },
      ];
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          xAxes: [
            {
              stacked: true,
              gridLines: { display: false },
              ticks: {
                fontSize: 11,
                fontColor: "#666",
                autoSkip: false,
                maxRotation: 30,
                minRotation: 0,
              },
              barPercentage: 0.55,
              categoryPercentage: 0.7,
            },
          ],
          yAxes: [
            {
              stacked: true,
              gridLines: {
                color: "rgba(0,0,0,0.05)",
                zeroLineColor: "rgba(0,0,0,0.1)",
              },
              ticks: {
                beginAtZero: true,
                precision: 0,
                stepSize: 1,
                fontSize: 11,
                fontColor: "#999",
              },
              scaleLabel: {
                display: true,
                labelString: "Candidates",
                fontSize: 11,
                fontColor: "#aaa",
              },
            },
          ],
        },
        tooltips: {
          mode: "index",
          intersect: false,
          backgroundColor: "rgba(255,255,255,0.97)",
          titleFontColor: "#333",
          titleFontSize: 12,
          bodyFontColor: "#555",
          bodyFontSize: 12,
          borderColor: "#e0e0e0",
          borderWidth: 1,
          callbacks: {
            afterBody(tooltipItems) {
              const total = tooltipItems.reduce(
                (sum, item) => sum + Number(item.yLabel || 0),
                0
              );
              return [`Total enrolled: ${total}`];
            },
            label(tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label || "";
              return `${label}: ${tooltipItem.yLabel}`;
            },
          },
        },
      };
    },
  },

  methods: {
    async loadCourses() {
      this.loading = true;
      try {
        const res = await course.getAdminCoursesDashboard();
        this.courses = res.data || [];
      } catch (err) {
        console.error("Failed to load courses for progress chart", err);
        this.courses = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>